import { useMemo, useState } from "react";
import { Paper, Title, Box, Text, SegmentedControl, Group } from "@mantine/core";
import { LineChart, BarChart } from "@mantine/charts";

/**
 * Traffic over time, from the server-side series.
 *
 * This used to bucket up to 1,000 raw events in the browser using the viewer's local timezone.
 * That capped the chart at the newest 1,000 events — so the wider the window, the more it
 * under-reported — and put the same brand's traffic on different days for different viewers.
 * Buckets now come from the server, aligned to UTC, over the whole window.
 */

const METRICS = {
  pageviews: { label: "Pageviews", colour: "indigo.6" },
  sessions: { label: "Sessions", colour: "teal.6" },
  events: { label: "All events", colour: "grape.6" },
};

export default function TrafficTimeline({ series, granularity, isLoading }) {
  const [metric, setMetric] = useState("pageviews");
  const [chartType, setChartType] = useState("line");

  const data = useMemo(() => {
    if (!series?.length) return [];
    return series.map((point) => ({
      bucket: formatBucket(point.bucket, granularity),
      pageviews: point.pageviews ?? 0,
      sessions: point.sessions ?? 0,
      events: point.events ?? 0,
    }));
  }, [series, granularity]);

  if (isLoading) {
    return (
      <Paper p="md" withBorder>
        <Title order={4}>Traffic Over Time</Title>
        <Box h={300} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
          <Text c="dimmed">Loading...</Text>
        </Box>
      </Paper>
    );
  }

  const isLine = chartType === "line";
  const Chart = isLine ? LineChart : BarChart;

  // curveType and withDots are LineChart-only. BarChart forwards unknown props to its root DOM
  // node, so passing them there produces React unknown-attribute warnings and stray attributes.
  const lineOnlyProps = isLine ? { curveType: "monotone", withDots: false } : {};

  return (
    <Paper p="md" withBorder>
      <Group justify="space-between" mb="md" wrap="wrap">
        <Title order={4}>Traffic Over Time</Title>
        <Group gap="sm">
          <SegmentedControl
            size="xs"
            value={metric}
            onChange={setMetric}
            data={Object.entries(METRICS).map(([value, m]) => ({ value, label: m.label }))}
          />
          <SegmentedControl
            size="xs"
            value={chartType}
            onChange={setChartType}
            data={[{ value: "line", label: "Line" }, { value: "bar", label: "Bar" }]}
          />
        </Group>
      </Group>

      <Box h={300}>
        {data.length > 0 ? (
          <Chart
            h={300}
            data={data}
            dataKey="bucket"
            series={[{ name: metric, label: METRICS[metric].label, color: METRICS[metric].colour }]}
            tickLine="x"
            {...lineOnlyProps}
          />
        ) : (
          <Box h="100%" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Text c="dimmed">No data available</Text>
          </Box>
        )}
      </Box>
    </Paper>
  );
}

/**
 * Buckets arrive as UTC instants. Rendered in UTC too — showing them in the viewer's timezone
 * would shift a bucket into the neighbouring day and reintroduce exactly the inconsistency the
 * server-side aggregation removed.
 */
function formatBucket(value, granularity) {
  const date = new Date(value);
  if (granularity === "hour") {
    return date.toLocaleString(undefined, {
      timeZone: "UTC", month: "short", day: "numeric", hour: "2-digit", hour12: false,
    });
  }
  return date.toLocaleDateString(undefined, { timeZone: "UTC", month: "short", day: "numeric" });
}
